/**
 * GalleryV2Toolbar
 *
 * Suchleiste, Filter und Ansichtsoptionen.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Grid3X3,
  List,
  LayoutGrid,
  SlidersHorizontal,
  X,
  Save,
  PanelRightClose,
  PanelRight,
  Grip,
  Download,
  Loader2,
} from 'lucide-react';
import { useGallerySearch, useFilterOptions, useSavedSearches } from '@/hooks/useGalleryV2';
import { bulkDownload } from '@/lib/api/gallery-v2';
import { Button, Input, Badge } from '@/components/v2/ui';
import { ApprovalModeButton } from '@/components/v2/tools/gallery';
import { cn } from '@/lib/utils';
import type { ViewMode } from './GalleryV2Layout';

interface GalleryV2ToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  selectedCount: number;
  selectedIds: Set<string>;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onClearSelection: () => void;
  perPage: number;
  onPerPageChange: (n: number) => void;
}

export function GalleryV2Toolbar({
  viewMode,
  onViewModeChange,
  selectedCount,
  selectedIds,
  sidebarOpen,
  onToggleSidebar,
  onClearSelection,
  perPage,
  onPerPageChange,
}: GalleryV2ToolbarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { params, updateParams, clearFilters, filtersApplied } = useGallerySearch();
  const { data: filterOptions } = useFilterOptions();
  const { createSearch } = useSavedSearches();
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(params.q || '');
  const [isDownloading, setIsDownloading] = useState(false);

  const activeFilterCount = Object.keys(filtersApplied).length;

  const handleOpenApproval = () => {
    navigate('/v2/tools/gallery/review');
  };

  const handleSearch = () => {
    updateParams({ q: searchInput || undefined });
  };

  const handleSaveSearch = async () => {
    const name = prompt(t('gallery.saveSearchName'));
    if (name) {
      await createSearch({ name, filters: filtersApplied });
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) return;

    setIsDownloading(true);
    try {
      const blob = await bulkDownload(Array.from(selectedIds));

      // Download triggern
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `goki_images_${selectedIds.size}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      onClearSelection();
    } catch (error) {
      console.error('Download failed:', error);
      alert(t('toast.error.downloading'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      {/* Main Toolbar */}
      <div className="flex items-center gap-4 px-4 py-3">
        {/* Search */}
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder={t('common.search')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10 pr-10"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('');
                updateParams({ q: undefined });
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Approval Mode Button */}
        <ApprovalModeButton onClick={handleOpenApproval} />

        {/* Filter Toggle */}
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {t('common.filter')}
          {activeFilterCount > 0 && (
            <Badge variant="default" className="ml-1">
              {activeFilterCount}
            </Badge>
          )}
        </Button>

        {/* Save Search */}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="icon" onClick={handleSaveSearch} title={t('common.save')}>
            <Save className="h-4 w-4" />
          </Button>
        )}

        {/* View Mode */}
        <div className="flex items-center border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
          <ViewModeButton
            active={viewMode === 'grid'}
            onClick={() => onViewModeChange('grid')}
            title="4-Spalten-Raster"
          >
            <Grid3X3 className="h-4 w-4" />
          </ViewModeButton>
          <ViewModeButton
            active={viewMode === 'grid5'}
            onClick={() => onViewModeChange('grid5')}
            title="5-Spalten-Raster"
          >
            <Grip className="h-4 w-4" />
          </ViewModeButton>
          <ViewModeButton
            active={viewMode === 'list'}
            onClick={() => onViewModeChange('list')}
            title="Listenansicht"
          >
            <List className="h-4 w-4" />
          </ViewModeButton>
          <ViewModeButton
            active={viewMode === 'bento'}
            onClick={() => onViewModeChange('bento')}
            title="Bento-Ansicht"
          >
            <LayoutGrid className="h-4 w-4" />
          </ViewModeButton>
        </div>

        {/* Sort */}
        <select
          value={`${params.sort_by}-${params.sort_order}`}
          onChange={(e) => {
            const [sort_by, sort_order] = e.target.value.split('-') as [
              'created_at' | 'display_name' | 'self_rating',
              'asc' | 'desc'
            ];
            updateParams({ sort_by, sort_order });
          }}
          className="h-9 px-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
        >
          <option value="created_at-desc">Neueste zuerst</option>
          <option value="created_at-asc">{t('gallery.oldestFirst')}</option>
          <option value="display_name-asc">Name A-Z</option>
          <option value="display_name-desc">Name Z-A</option>
          <option value="self_rating-desc">Bewertung (Hoch → Niedrig)</option>
          <option value="self_rating-asc">Bewertung (Niedrig → Hoch)</option>
        </select>

        {/* Per-Page */}
        <select
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
          className="h-9 px-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
          aria-label="Bilder pro Seite"
        >
          <option value={24}>24 / Seite</option>
          <option value={48}>48 / Seite</option>
          <option value={96}>96 / Seite</option>
        </select>

        {/* Sidebar Toggle – ganz rechts */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          title={sidebarOpen ? t('gallery.closeSidebar') : t('gallery.openSidebar')}
          aria-label={sidebarOpen ? t('gallery.closeSidebar') : t('gallery.openSidebar')}
        >
          {sidebarOpen ? (
            <PanelRightClose className="h-5 w-5" />
          ) : (
            <PanelRight className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="px-4 py-3 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50">
          <div className="flex flex-wrap gap-3">
            {/* Template Filter */}
            <FilterSelect
              label="Template"
              value={params.template_id || ''}
              onChange={(v) => updateParams({ template_id: v || undefined })}
              options={[
                { value: '', label: 'Alle Templates' },
                ...(filterOptions?.templates || []).map((t) => ({
                  value: t.id,
                  label: t.name,
                })),
              ]}
            />

            {/* Ratio Filter */}
            <FilterSelect
              label={t('gallery.aspectRatioLabel')}
              value={params.ratio || ''}
              onChange={(v) => updateParams({ ratio: v || undefined })}
              options={[
                { value: '', label: 'Alle' },
                ...(filterOptions?.ratios || []).map((r) => ({
                  value: r,
                  label: r,
                })),
              ]}
            />

            {/* Format Filter */}
            <FilterSelect
              label="Format"
              value={params.format || ''}
              onChange={(v) => updateParams({ format: v || undefined })}
              options={[
                { value: '', label: 'Alle' },
                ...(filterOptions?.formats || []).map((f) => ({
                  value: f,
                  label: f.toUpperCase(),
                })),
              ]}
            />

            {/* Creator Filter */}
            <FilterSelect
              label="Ersteller"
              value={params.created_by || ''}
              onChange={(v) => updateParams({ created_by: v || undefined })}
              options={[
                { value: '', label: 'Alle' },
                ...(filterOptions?.users || []).map((u) => ({
                  value: u.id,
                  label: u.username || u.email,
                })),
              ]}
            />

            {/* Approval Status Filter - Converted to String for FilterSelect */}
            <FilterSelect
              label={t('common.status')}
              value={Array.isArray(params.approval_status) ? params.approval_status.join(',') : (params.approval_status || '')}
              onChange={(v) => {
                if (v === '') {
                  updateParams({ approval_status: ['pending', 'approved'] });
                } else {
                  const statuses = v.split(',').filter(
                    (s): s is 'pending' | 'approved' | 'rejected' =>
                      s === 'pending' || s === 'approved' || s === 'rejected'
                  );
                  updateParams({ approval_status: statuses });
                }
              }}
              options={[
                { value: 'pending,approved', label: t('gallery.allExceptRejected') },
                { value: 'pending', label: t('common.pending') },
                { value: 'approved', label: t('common.approve') },
                { value: 'rejected', label: t('common.reject') },
              ]}
            />

            {/* Minimum Rating Filter */}
            <FilterSelect
              label="Min. Bewertung"
              value={params.min_rating?.toString() || ''}
              onChange={(v) => updateParams({ min_rating: v ? parseInt(v, 10) : undefined })}
              options={[
                { value: '', label: 'Alle' },
                { value: '1', label: '⭐ 1+' },
                { value: '2', label: '⭐ 2+' },
                { value: '3', label: '⭐ 3+' },
                { value: '4', label: '⭐ 4+' },
                { value: '5', label: '⭐ 5' },
              ]}
            />

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                {t('gallery.resetFilters')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Selection Bar */}
      {selectedCount > 0 && (
        <div className="px-4 py-2 border-t border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
              {t('gallery.imagesSelected', { count: selectedCount, suffix: selectedCount !== 1 ? 'er' : '' })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkDownload}
              disabled={isDownloading || selectedCount > 50}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              {isDownloading ? t('common.uploading') : t('common.download')}
            </Button>
            {selectedCount > 50 && (
              <span className="text-xs text-red-500">Max. 50 Bilder</span>
            )}
            <Button variant="ghost" size="sm" onClick={onClearSelection}>
              <X className="h-4 w-4 mr-1" />
              {t('gallery.closeSelection')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

interface ViewModeButtonProps {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

function ViewModeButton({ active, onClick, title, children }: ViewModeButtonProps) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cn(
        'p-2 transition-colors',
        active
          ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100'
          : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
      )}
    >
      {children}
    </button>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-sm text-neutral-500">{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 px-2 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
